import { Legend, STATUS_TONES, statusLegendItems, statusTone } from '../../components/Legend';
import React, { useEffect, useState, useMemo } from 'react';
import { ClipboardList } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import * as dashboardApi from '../../api/dashboardApi';
import * as searchApi from '../../api/searchApi';
import { BRAND_BLUE_SOFT, BRAND_ORANGE, BRAND_ORANGE_DEEP, BRAND_ORANGE_LIGHT } from '../../design-system/chartColors';

interface AllRegulationsTableProps {
  jurisdictionId?: string;
  jurisdictionType?: string;
  regulationType?: string;
  countryId?: string;
  jurisdictions: dashboardApi.Jurisdiction[];
  isLoading?: boolean;
  selectedTimeRange?: 'All' | '24H' | '7D' | '30D' | '90D' | '1Y' | 'Custom';
  dateFilterType?: 'last_action_date' | 'published_date' | 'both' | 'date_added';
  customStartDate?: string;
  customEndDate?: string;
  enforcement?: 'enforced' | 'pending' | 'inactive';
  adminMode?: boolean;   // admin context: show soft-delete (hide/restore) controls
  showHidden?: boolean;  // admin: list hidden records (for restore) instead of visible ones
}

type SortOption = 'relevant' | 'newest' | 'oldest' | 'az' | 'za';

/* ── Skeleton card ── */
const SkeletonCard: React.FC<{ isDark: boolean }> = ({ isDark }) => (
  <div style={{
    borderRadius: 12,
    background: isDark ? 'rgba(255,255,255,0.05)' : '#f2f4f7',
    height: 280,
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    overflow: 'hidden',
    position: 'relative',
  }}>
    <style>{`@keyframes shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(200%)}}`}</style>
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 12 }}>
      <div style={{
        position: 'absolute', top: 0, bottom: 0, width: '50%',
        background: `rgba(255,255,255,0.04)`,
        animation: 'shimmer 1.6s ease-in-out infinite',
      }} />
    </div>
    <div style={{ height: 18, borderRadius: 4, background: isDark ? 'rgba(255,255,255,0.12)' : '#e0eaff', width: '85%' }} />
    <div style={{ height: 18, borderRadius: 4, background: isDark ? 'rgba(255,255,255,0.08)' : '#e0eaff', width: '60%' }} />
    <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
      <div style={{ height: 24, width: 65, borderRadius: 6, background: isDark ? 'rgba(255,255,255,0.08)' : '#e0eaff' }} />
      <div style={{ height: 24, width: 90, borderRadius: 6, background: isDark ? 'rgba(255,255,255,0.06)' : '#e0eaff' }} />
      <div style={{ height: 24, width: 55, borderRadius: 6, background: isDark ? 'rgba(255,255,255,0.05)' : '#e0eaff' }} />
    </div>
    <div style={{ height: 13, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.06)' : '#e0eaff', width: '100%' }} />
    <div style={{ height: 13, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.05)' : '#e0eaff', width: '90%' }} />
    <div style={{ height: 13, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.04)' : '#e0eaff', width: '72%' }} />
    <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ height: 28, width: 100, borderRadius: 6, background: isDark ? 'rgba(255,255,255,0.06)' : '#eaecf0' }} />
      <div style={{ height: 28, width: 32, borderRadius: 6, background: isDark ? 'rgba(255,255,255,0.04)' : '#eaecf0' }} />
    </div>
  </div>
);

export const AllRegulationsTable: React.FC<AllRegulationsTableProps> = ({
  jurisdictionId: initialJurisdictionId,
  jurisdictionType: initialJurisdictionType,
  regulationType: initialRegulationType,
  countryId,
  jurisdictions,
  isLoading: parentIsLoading = false,
  selectedTimeRange,
  dateFilterType,
  customStartDate,
  customEndDate,
  enforcement,
  adminMode = false,
  showHidden = false,
}) => {
  const { theme } = useTheme();
  const { token } = useAuth();
  const location = useLocation();
  const isDark = theme === 'dark';
  const [regulations, setRegulations] = useState<dashboardApi.RegulationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 12;
  /** The most matches a search returns (the search API's own maximum). */
  const SEARCH_WINDOW = 100;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  // Signed-in accounts default to their own ranking (SRS #22); anonymous to newest.
  const [sortBy, setSortBy] = useState<SortOption>(() => (localStorage.getItem('policyora_token') ? 'relevant' : 'newest'));
  const [showSaved, setShowSaved] = useState(false);
  const [bookmarkData, setBookmarkData] = useState<Record<string, dashboardApi.RegulationSummary>>(() => {
    try {
      // Remove stale ID-only key from old code
      localStorage.removeItem('compass_bookmarks');
      const s = localStorage.getItem('compass_bookmark_data');
      return s ? JSON.parse(s) : {};
    } catch { return {}; }
  });

  // Derived — always in sync with bookmarkData
  const bookmarked = useMemo(() => new Set<string>(Object.keys(bookmarkData)), [bookmarkData]);
  const [hidingId, setHidingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [alertEmailsEnabled, setAlertEmailsEnabled] = useState(false);
  const [alertPrefsLoading, setAlertPrefsLoading] = useState(false);
  const [alertPrefsSaving, setAlertPrefsSaving] = useState(false);
  const [alertSendingNow, setAlertSendingNow] = useState(false);
  const [alertPrefsMessage, setAlertPrefsMessage] = useState('');

  const show24hAlertsControl = Boolean(token) && (
    location.pathname.startsWith('/enterprise/dashboard') ||
    location.pathname.startsWith('/startup/dashboard') ||
    location.pathname.startsWith('/government/dashboard') ||
    location.pathname.startsWith('/admin/dashboard')
  );

  const toggleBookmark = (reg: dashboardApi.RegulationSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    setBookmarkData(prev => {
      const next = { ...prev };
      if (next[reg.id]) delete next[reg.id];
      else next[reg.id] = reg;
      try { localStorage.setItem('compass_bookmark_data', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Admin soft-delete (Phase-1 #10): hide a record (or restore it in the hidden
  // view). On success the card leaves the current list (it moved to the other
  // view). Reversible — backend just toggles is_hidden.
  const handleHideToggle = async (reg: dashboardApi.RegulationSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    const action = showHidden ? 'unhide' : 'hide';
    if (!showHidden && !window.confirm('Hide this regulation from listings? You can restore it later from "Show hidden".')) return;
    setHidingId(reg.id);
    try {
      const resp = await fetch(`/api/v1/admin/regulations/${reg.id}/${action}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!resp.ok) throw new Error('failed');
      setRegulations(rs => rs.filter(r => r.id !== reg.id));
      setTotalCount(c => Math.max(0, c - 1));
    } catch {
      alert(`Failed to ${action} this regulation.`);
    } finally {
      setHidingId(null);
    }
  };

  // Admin hard-delete (QA #10): permanently remove the artifact + all derived
  // rows + its embedding from the DB/search. Irreversible — double-confirmed.
  const handleHardDelete = async (reg: dashboardApi.RegulationSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Permanently DELETE "${reg.title}"?\n\nThis removes it from the database, search and RAG and CANNOT be undone. (Use "Hide" instead for a reversible removal.)`)) return;
    setDeletingId(reg.id);
    try {
      const resp = await fetch(`/api/v1/admin/regulations/${reg.id}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!resp.ok) throw new Error('failed');
      setRegulations(rs => rs.filter(r => r.id !== reg.id));
      setTotalCount(c => Math.max(0, c - 1));
    } catch {
      alert('Failed to delete this regulation.');
    } finally {
      setDeletingId(null);
    }
  };

  // SRS #20: colours come from the shared status vocabulary (components/Legend.tsx).
  const getStatusStyle = (status?: string) => {
    if (!status) return null;
    const tone = statusTone(status);
    const c = STATUS_TONES[tone].color;
    const rgba = (a: number) => `rgba(${parseInt(c.slice(1, 3), 16)}, ${parseInt(c.slice(3, 5), 16)}, ${parseInt(c.slice(5, 7), 16)}, ${a})`;
    const light: Record<string, { bg: string; color: string; border: string }> = {
      in_force: { bg: '#ecfdf3', color: '#039855', border: '#12b76a' },
      pending: { bg: '#fffaeb', color: '#b54708', border: '#fde68a' },
      inactive: { bg: '#fef3f2', color: '#d92d20', border: '#fecdca' },
      unknown: { bg: '#f9fafb', color: '#667085', border: '#eaecf0' },
    };
    return isDark
      ? { bg: rgba(0.15), color: c, border: rgba(0.3), dot: c, accent: c }
      : { ...light[tone], dot: c, accent: c };
  };

  /* Debounce */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [countryId, initialJurisdictionId, initialJurisdictionType, initialRegulationType, enforcement, debouncedSearchQuery, sortBy]);

  useEffect(() => {
    if (!token || !show24hAlertsControl) return;

    let isMounted = true;
    setAlertPrefsLoading(true);
    dashboardApi.getRegulationAlertPreference(token)
      .then((preference) => {
        if (!isMounted) return;
        setAlertEmailsEnabled(Boolean(preference.enabled));
        setAlertPrefsMessage('');
      })
      .catch(() => {
        if (!isMounted) return;
        setAlertPrefsMessage('Could not load 24-hour alert email preference right now.');
      })
      .finally(() => {
        if (isMounted) setAlertPrefsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [token, show24hAlertsControl]);

  useEffect(() => {
    const fetchRegulations = async () => {
      setIsLoading(true);
      try {
        if (debouncedSearchQuery.trim()) {
          // The search endpoint has no offset, so it cannot return page 2 on its own. It returns
          // its best matches (at most SEARCH_WINDOW), and the page is cut from those. Before this,
          // every page asked for the same first results.
          const searchResponse = await searchApi.searchRegulations({
            query: debouncedSearchQuery.trim(),
            size: SEARCH_WINDOW,
            jurisdiction_ids: initialJurisdictionId ? [initialJurisdictionId] : undefined,
          });
          const matches = searchResponse.results.map(result => ({
            id: result.id,
            title: result.title,
            title_short: result.title,
            summary: result.summary || '',
            jurisdiction_name: result.jurisdiction || '',
            jurisdiction_code: result.jurisdiction_code || '',
            regulation_type: result.bill_type_label || result.bill_type || '',
            status: '',
            last_action_date: result.action_date || undefined,
            effective_date: result.action_date || undefined,
            source_url: '',
          }));
          const searchOffset = (currentPage - 1) * itemsPerPage;
          setRegulations(matches.slice(searchOffset, searchOffset + itemsPerPage));
          setTotalCount(Math.min(searchResponse.total_results, matches.length));
        } else {
          const offset = (currentPage - 1) * itemsPerPage;
          const params: any = {
            limit: itemsPerPage, offset,
            // asc for Oldest (by date) AND A→Z (by title); desc for Newest / Z→A.
            // Previously only 'oldest' mapped to asc, so 'az' and 'za' both sent
            // desc → A→Z and Z→A returned identical order.
            sort_order: (sortBy === 'oldest' || sortBy === 'az') ? 'asc' : 'desc',
            sort_by: sortBy === 'relevant' ? 'relevance' : (sortBy === 'az' || sortBy === 'za') ? 'title' : 'date',
            token,
          };
          if (initialJurisdictionId) {
            if (initialJurisdictionType) {
              params.jurisdiction_type = initialJurisdictionType;
              // Check if it's a UUID (for real jurisdictions) or a code (for supranationals)
              if (initialJurisdictionId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                params.jurisdiction_id = initialJurisdictionId;
              } else {
                params.jurisdiction_code = initialJurisdictionId;
              }
            } else {
              // Check if it's a UUID (for real jurisdictions) or a code (for supranationals)
              if (initialJurisdictionId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                params.jurisdiction_id = initialJurisdictionId;
              } else {
                params.jurisdiction_code = initialJurisdictionId;
              }
            }
          } else if (initialJurisdictionType) {
            params.jurisdiction_type = initialJurisdictionType;
          } else if (countryId) {
            params.country_id = countryId;
          }
          if (initialRegulationType) params.regulation_type = initialRegulationType;
          if (enforcement) params.enforcement = enforcement;
          if (adminMode && showHidden) params.include_hidden = true;
          try {
            if (selectedTimeRange === 'Custom') {
              if (customStartDate && customEndDate) {
                params.start_date = customStartDate;
                params.end_date = customEndDate;
              }
            } else if (selectedTimeRange && selectedTimeRange !== 'All') {
              const daysMap: Record<string, number> = { '24H': 1, '7D': 7, '30D': 30, '90D': 90, '1Y': 365 };
              params.days = daysMap[selectedTimeRange] || 7;
            }
            if (dateFilterType) params.date_type = dateFilterType;
          } catch {}
          const response = await dashboardApi.getAllRegulations(params as any);
          setRegulations(response.regulations);
          setTotalCount(response.total);
        }
      } catch (error) {
        console.error('[AllRegulationsTable] Error fetching regulations:', error);
        setRegulations([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRegulations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId, currentPage, initialJurisdictionId, initialJurisdictionType, initialRegulationType, enforcement, adminMode, showHidden, selectedTimeRange, dateFilterType, customStartDate, customEndDate, jurisdictions, debouncedSearchQuery, sortBy]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const FORMAT_SUFFIX_MAP: Record<string, string> = {
    TXT: 'Text Document', PDF: 'PDF Document', HTML: 'Web Document',
    XML: 'XML Document', DOC: 'Word Document',
  };

  const formatDocType = (type?: string): string => {
    if (!type) return 'Document';
    const parenMatch = type.match(/\(([^)]+)\)/);
    const base = type.replace(/\s*\([^)]*\)/g, '').trim();
    if (parenMatch && parenMatch[1].length > base.length) return parenMatch[1];
    const parts = base.split('_');
    const last = parts[parts.length - 1].toUpperCase();
    if (FORMAT_SUFFIX_MAP[last] && parts.length > 1) return FORMAT_SUFFIX_MAP[last];
    return base.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  const highlightSearchTerm = (text: string | undefined, searchTerm: string): React.ReactNode => {
    if (!searchTerm.trim() || !text) return text || '';
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#c7d7fe', padding: '1px 2px', borderRadius: 2 }}>{part}</span>
      ) : <span key={index}>{part}</span>
    );
  };

  /* Saved regulations from localStorage */
  const savedRegulations = useMemo(
    () => Object.values(bookmarkData),
    [bookmarkData]
  );

  const handleToggleAlertEmails = async () => {
    if (!token) {
      setAlertPrefsMessage('Please sign in to manage regulation alerts.');
      return;
    }

    setAlertPrefsSaving(true);
    setAlertPrefsMessage('');
    try {
      const result = await dashboardApi.updateRegulationAlertPreference(token, {
        enabled: !alertEmailsEnabled,
      });
      setAlertEmailsEnabled(Boolean(result.enabled));
      if (result.detail) setAlertPrefsMessage(result.detail);
    } catch {
      setAlertPrefsMessage('Could not save the regulation alert preference right now.');
    } finally {
      setAlertPrefsSaving(false);
    }
  };

  const handleSendAlertNow = async () => {
    if (!token) {
      setAlertPrefsMessage('Please sign in to send a regulation alert email.');
      return;
    }

    setAlertSendingNow(true);
    setAlertPrefsMessage('');
    try {
      const result = await dashboardApi.sendRegulationAlertPreviewNow(token);
      setAlertPrefsMessage(result.detail || 'Regulation alert email sent.');
    } catch {
      setAlertPrefsMessage('Could not send the regulation alert email right now.');
    } finally {
      setAlertSendingNow(false);
    }
  };

  const showEmptyState = !showSaved && !isLoading && regulations.length === 0;

  const sortBtnBase: React.CSSProperties = {
    padding: '7px 13px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#eaecf0'}`,
    whiteSpace: 'nowrap' as const,
  };

  const sortLabels: Record<SortOption, string> = {
    relevant: '★ Relevant',
    newest: '↓ Newest',
    oldest: '↑ Oldest',
    az: 'A → Z',
    za: 'Z → A',
  };


  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {show24hAlertsControl && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.9rem', flexWrap: 'wrap',
          background: isDark ? 'rgba(97, 114, 243, 0.08)' : '#ecfdf3',
          border: `1px solid ${isDark ? 'rgba(97, 114, 243, 0.24)' : '#ecfdf3'}`,
          borderRadius: 12,
          padding: '12px 14px',
        }}>
          <div style={{ minWidth: 240, flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: isDark ? '#ecfdf3' : BRAND_ORANGE_DEEP }}>
              24h Regulation Alerts
            </div>
            <div style={{ fontSize: 12, marginTop: 2, color: isDark ? '#12b76a' : BRAND_ORANGE_DEEP }}>
              Keep this on to receive an email whenever Policyora finds new regulations in the last 24 hours.
            </div>
            {alertPrefsMessage && (
              <div style={{ fontSize: 11.5, marginTop: 6, color: isDark ? '#ecfdf3' : BRAND_ORANGE_DEEP }}>
                {alertPrefsMessage}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleToggleAlertEmails}
              disabled={alertPrefsLoading || alertPrefsSaving}
              style={{
                ...sortBtnBase,
                background: alertEmailsEnabled ? (isDark ? 'rgba(97, 114, 243, 0.2)' : '#ecfdf3') : (isDark ? 'rgba(255,255,255,0.04)' : '#ffffff'),
                color: alertEmailsEnabled ? (isDark ? '#12b76a' : BRAND_ORANGE_DEEP) : (isDark ? '#d0d5dd' : '#475467'),
                borderColor: alertEmailsEnabled ? (isDark ? 'rgba(97, 114, 243, 0.45)' : '#12b76a') : (isDark ? 'rgba(255,255,255,0.1)' : '#d0d5dd'),
                display: 'inline-flex', alignItems: 'center', gap: 8,
                opacity: alertPrefsLoading || alertPrefsSaving ? 0.75 : 1,
              }}
            >
              <span style={{
                position: 'relative',
                width: 34,
                height: 18,
                borderRadius: 999,
                background: alertEmailsEnabled ? '#12b76a' : '#d0d5dd',
                display: 'inline-block',
                transition: 'background 0.2s ease',
              }}>
                <span style={{
                  position: 'absolute',
                  top: 2,
                  left: alertEmailsEnabled ? 18 : 2,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: 'var(--ds-surface)',
                  transition: 'left 0.2s ease',
                }} />
              </span>
              {alertPrefsSaving ? 'Saving…' : alertEmailsEnabled ? 'On' : 'Off'}
            </button>

            <button
              type="button"
              onClick={handleSendAlertNow}
              disabled={alertSendingNow || alertPrefsLoading}
              style={{
                ...sortBtnBase,
                background: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff',
                color: isDark ? '#eaecf0' : '#1d2939',
                opacity: alertSendingNow || alertPrefsLoading ? 0.7 : 1,
              }}
            >
              {alertSendingNow ? 'Sending…' : 'Send now'}
            </button>
          </div>
        </div>
      )}

      {/* ── Top bar: search + sort + count ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.75)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
        borderRadius: 12, padding: '10px 14px',
        backdropFilter: 'blur(8px)',
      }}>
        {/* Search */}
        <div style={{ flex: 1, minWidth: 260, maxWidth: 480, position: 'relative' }}>
          <svg style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', opacity: 0.45, pointerEvents: 'none' }}
            width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#98a2b3' : '#667085'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search regulations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 14px 9px 38px',
              borderRadius: 10,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#eaecf0'}`,
              background: isDark ? 'rgba(255,255,255,0.07)' : '#FFFFFF',
              color: isDark ? '#f9fafb' : '#1d2939',
              fontSize: 13.5,
              outline: 'none',
              transition: 'all 0.2s ease',
              boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = isDark ? 'rgba(68,76,231,0.6)' : BRAND_ORANGE_LIGHT;
              e.target.style.boxShadow = '0 0 0 3px rgba(68,76,231,0.12)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.14)' : '#eaecf0';
              e.target.style.boxShadow = isDark ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)';
            }}
          />
        </div>

        {/* Sort buttons — hidden in saved view */}
        {!showSaved && (
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {(['relevant', 'newest', 'oldest', 'az', 'za'] as SortOption[]).map(opt => (
              <button
                key={opt}
                onClick={() => setSortBy(opt)}
                style={{
                  ...sortBtnBase,
                  background: sortBy === opt
                    ? (isDark ? 'rgba(68,76,231,0.2)' : '#f0f5ff')
                    : (isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF'),
                  color: sortBy === opt
                    ? (isDark ? BRAND_BLUE_SOFT : BRAND_ORANGE)
                    : (isDark ? '#98a2b3' : '#667085'),
                  borderColor: sortBy === opt
                    ? (isDark ? 'rgba(68,76,231,0.4)' : '#c7d7fe')
                    : (isDark ? 'rgba(255,255,255,0.1)' : '#eaecf0'),
                }}
              >
                {sortLabels[opt]}
              </button>
            ))}
          </div>
        )}

        {/* Saved toggle */}
        <button
          onClick={() => setShowSaved(s => !s)}
          style={{
            ...sortBtnBase,
            background: showSaved
              ? (isDark ? 'rgba(68, 76, 231, 0.15)' : '#f0f5ff')
              : (isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF'),
            color: showSaved
              ? (isDark ? BRAND_ORANGE : BRAND_ORANGE_DEEP)
              : (isDark ? '#98a2b3' : '#667085'),
            borderColor: showSaved
              ? (isDark ? 'rgba(68, 76, 231, 0.35)' : '#c7d7fe')
              : (isDark ? 'rgba(255,255,255,0.1)' : '#eaecf0'),
            display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24"
            fill={showSaved ? 'currentColor' : 'none'}
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
          Saved {bookmarked.size > 0 && `(${bookmarked.size})`}
        </button>

        {/* Active and New counts beside filters */}
        {!showSaved && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            fontSize: 12.5, fontWeight: 600,
            color: isDark ? '#98a2b3' : '#667085',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            <span style={{
              color: isDark ? '#f2f4f7' : '#101828',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: isDark ? '#667085' : '#98a2b3' }} />
              {totalCount.toLocaleString()} regulations
            </span>
          </div>
        )}
      </div>

      {/* ── Saved view header ── */}
      {showSaved && (
        <div style={{ fontSize: 13, color: 'var(--ds-text-muted)', fontWeight: 500 }}>
          {savedRegulations.length === 0
            ? 'No saved regulations yet — click ☆ on any card to save it.'
            : <><span style={{ fontWeight: 700, color: isDark ? '#f2f4f7' : '#101828' }}>{savedRegulations.length}</span> saved regulation{savedRegulations.length !== 1 ? 's' : ''}</>
          }
        </div>
      )}

      {/* SRS #20: the status colours on the cards, explained once */}
      <Legend compact dark={isDark} title="Status" items={statusLegendItems()} />

      {/* ── Grid: skeletons while loading, else cards ── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: '1.25rem', alignContent: 'start',
        opacity: !showSaved && isLoading && regulations.length > 0 ? 0.55 : 1,
        transition: 'opacity 0.2s ease',
        pointerEvents: isLoading && !showSaved ? 'none' : 'auto',
      }}>
        {!showSaved && isLoading && regulations.length === 0
          ? Array.from({ length: itemsPerPage }).map((_, i) => <SkeletonCard key={i} isDark={isDark} />)
          : showEmptyState
            ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: isDark ? '#98a2b3' : '#667085', fontSize: 15 }}>
                <div style={{ marginBottom: '1rem', opacity: 0.3 }}><ClipboardList size={48} aria-hidden /></div>
                No regulations found for the selected filters.
              </div>
            )
            : (showSaved ? savedRegulations : regulations).map((reg) => {
              const displayDate = reg.last_action_date || reg.effective_date;
              const statusStyle = getStatusStyle(reg.status);
              const isBookmarked = bookmarked.has(reg.id);

              return (
                <div
                  key={reg.id}
                  onClick={() => window.open(`/regulations/${reg.id}`, '_blank')}
                  style={{
                    borderRadius: 12,
                    background: isDark
                      ? 'rgba(255,255,255,0.06)'
                      : '#fcfcfd',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e0eaff'}`,
                    borderTop: `2px solid ${isDark ? 'rgba(68,76,231,0.3)' : '#c7d7fe'}`,
                    padding: '15px 18px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'box-shadow 0.22s ease, transform 0.22s ease, border-color 0.22s ease',
                    boxShadow: isDark
                      ? '0 2px 12px rgba(0,0,0,0.3)'
                      : '0 1px 4px rgba(16,24,40,0.06)',
                    height: 280,
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLDivElement;
                    el.style.transform = 'translateY(-3px)';
                    el.style.boxShadow = isDark ? '0 12px 32px rgba(0,0,0,0.5)' : '0 6px 18px rgba(68,76,231,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLDivElement;
                    el.style.transform = 'translateY(0)';
                    el.style.boxShadow = isDark ? '0 2px 12px rgba(0,0,0,0.3)' : '0 1px 4px rgba(16,24,40,0.06)';
                  }}
                >
                  {/* ── Title row ── */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <h4 style={{
                      margin: 0, fontSize: 14.5, fontWeight: 700,
                      color: isDark ? '#f2f4f7' : '#101828',
                      lineHeight: 1.35,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      flex: 1,
                    }}>
                      {highlightSearchTerm(reg.title || reg.title_short, debouncedSearchQuery)}
                    </h4>
                    <a
                      href={`/regulations/${reg.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="Open in new tab"
                      style={{
                        flexShrink: 0, color: isDark ? '#98a2b3' : '#667085',
                        display: 'flex', alignItems: 'center',
                        transition: 'color 0.15s ease',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = isDark ? BRAND_BLUE_SOFT : BRAND_ORANGE_LIGHT; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = isDark ? '#98a2b3' : '#667085'; }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </a>
                  </div>

                  {/* ── Tags row: status + jurisdiction + type + NEW ── */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Status badge */}
                    {reg.status && statusStyle && (
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                        background: isDark ? 'rgba(255,255,255,0.08)' : '#344054',
                        color: isDark ? '#eaecf0' : 'var(--ds-text-on-dark)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#344054'}`,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        letterSpacing: '0.02em',
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusStyle.dot, flexShrink: 0 }} />
                        {reg.status.charAt(0).toUpperCase() + reg.status.slice(1)}
                      </span>
                    )}

                    {/* Jurisdiction */}
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 6,
                      background: isDark ? 'rgba(255,255,255,0.08)' : '#344054',
                      color: isDark ? '#eaecf0' : 'var(--ds-text-on-dark)',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#344054'}`,
                      letterSpacing: '0.01em',
                    }}>
                      {reg.jurisdiction_name || reg.jurisdiction_code}
                      {reg.jurisdiction_name && reg.jurisdiction_code && reg.jurisdiction_name !== reg.jurisdiction_code && (
                        <span style={{ fontWeight: 400, marginLeft: 4, opacity: 0.65 }}>({reg.jurisdiction_code})</span>
                      )}
                    </span>

                    {/* Type */}
                    {reg.regulation_type && (
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 6,
                        background: isDark ? 'rgba(255,255,255,0.06)' : '#f2f4f7',
                        color: isDark ? '#d0d5dd' : '#344054',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#d0d5dd'}`,
                        letterSpacing: '0.01em',
                      }}>
                        {formatDocType(reg.regulation_type)}
                      </span>
                    )}

                  </div>

                  {/* ── Summary ── */}
                  <p style={{
                    margin: 0, fontSize: 12.5, color: isDark ? '#98a2b3' : '#667085',
                    lineHeight: 1.6,
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    flex: 1,
                  }}>
                    {highlightSearchTerm(reg.summary || '', debouncedSearchQuery)}
                  </p>
                  {reg.relevance_reasons && reg.relevance_reasons.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}
                         title={reg.relevance != null ? `Relevance ${(reg.relevance * 100).toFixed(0)}%` : undefined}>
                      {reg.relevance_reasons.map((why) => (
                        <span key={why} style={{
                          fontSize: 10.5, padding: '2px 7px', borderRadius: 999,
                          background: isDark ? 'rgba(68,76,231,0.18)' : '#eef2ff',
                          color: isDark ? BRAND_BLUE_SOFT : BRAND_ORANGE,
                        }}>{why}</span>
                      ))}
                    </div>
                  )}

                  {/* ── Footer: date + bookmark ── */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f2f4f7'}`,
                    paddingTop: 10,
                    marginTop: 'auto',
                  }}>
                    {displayDate ? (
                      <span style={{
                        fontSize: 11.5, fontWeight: 500,
                        color: isDark ? '#98a2b3' : '#475467',
                        display: 'flex', alignItems: 'center', gap: 5,
                      }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        {new Date(displayDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    ) : <span />}

                    {/* Bookmark button */}
                    <button
                      onClick={(e) => toggleBookmark(reg, e)}
                      title={isBookmarked ? 'Remove bookmark' : 'Bookmark'}
                      style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        padding: '4px 6px', borderRadius: 6,
                        color: isBookmarked
                          ? (isDark ? BRAND_ORANGE : BRAND_ORANGE)
                          : (isDark ? '#98a2b3' : '#667085'),
                        transition: 'color 0.15s ease, background 0.15s ease',
                        display: 'flex', alignItems: 'center',
                      }}
                      onMouseEnter={(e) => {
                        const btn = e.currentTarget;
                        btn.style.color = isDark ? BRAND_ORANGE : BRAND_ORANGE;
                        btn.style.background = isDark ? 'rgba(68, 76, 231, 0.1)' : '#f0f5ff';
                      }}
                      onMouseLeave={(e) => {
                        const btn = e.currentTarget;
                        btn.style.color = isBookmarked ? (isDark ? BRAND_ORANGE : BRAND_ORANGE) : (isDark ? '#98a2b3' : '#667085');
                        btn.style.background = 'transparent';
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24"
                        fill={isBookmarked ? 'currentColor' : 'none'}
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                    </button>

                    {/* Admin soft-delete: hide / restore (Phase-1 #10) */}
                    {adminMode && (
                      <button
                        onClick={(e) => handleHideToggle(reg, e)}
                        disabled={hidingId === reg.id}
                        title={showHidden ? 'Restore to listings' : 'Hide from listings'}
                        style={{
                          border: 'none', background: 'transparent',
                          cursor: hidingId === reg.id ? 'wait' : 'pointer',
                          padding: '4px 8px', borderRadius: 6, marginLeft: 4,
                          fontSize: 12, fontWeight: 600,
                          color: showHidden ? '#039855' : '#d92d20',
                          opacity: hidingId === reg.id ? 0.5 : 1,
                        }}
                      >
                        {hidingId === reg.id ? '…' : (showHidden ? 'Restore' : 'Hide')}
                      </button>
                    )}
                    {adminMode && (
                      <button
                        onClick={(e) => handleHardDelete(reg, e)}
                        disabled={deletingId === reg.id}
                        title="Permanently delete (irreversible)"
                        style={{
                          border: 'none', background: 'transparent',
                          cursor: deletingId === reg.id ? 'wait' : 'pointer',
                          padding: '4px 8px', borderRadius: 6, marginLeft: 4,
                          fontSize: 12, fontWeight: 600, color: 'var(--ds-danger-text)',
                          opacity: deletingId === reg.id ? 0.5 : 1,
                        }}
                      >
                        {deletingId === reg.id ? '…' : 'Delete'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
        }
      </div>

      {/* ── Pagination (hidden in saved view) ── */}
      {!showSaved && totalPages > 1 && (() => {
        const pageNums: (number | null)[] = totalPages <= 7
          ? Array.from({ length: totalPages }, (_, i) => i + 1)
          : currentPage <= 4 ? [1,2,3,4,5,null,totalPages]
          : currentPage >= totalPages-3 ? [1,null,totalPages-4,totalPages-3,totalPages-2,totalPages-1,totalPages]
          : [1,null,currentPage-1,currentPage,currentPage+1,null,totalPages];
        const btnStyle = (active: boolean, disabled: boolean): React.CSSProperties => ({
          padding: '0.45rem 0.7rem', borderRadius: 8, fontSize: '0.85rem',
          border: `1px solid ${active ? (isDark ? BRAND_ORANGE_LIGHT : BRAND_ORANGE) : (isDark ? 'rgba(255,255,255,0.1)' : '#eaecf0')}`,
          background: active ? (isDark ? 'rgba(68,76,231,0.2)' : '#f0f5ff') : (isDark ? 'transparent' : 'white'),
          color: active ? (isDark ? BRAND_BLUE_SOFT : BRAND_ORANGE) : (isDark ? '#98a2b3' : '#344054'),
          fontWeight: active ? 700 : 500, cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1, transition: 'all 0.15s ease', minWidth: '38px',
        });
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '0.4rem', paddingTop: '1.25rem', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#eaecf0'}` }}>
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p-1))} style={btnStyle(false, currentPage === 1)}>← Prev</button>
            {pageNums.map((page, idx) => page === null
              ? <span key={`e${idx}`} style={{ padding: '0 4px', color: isDark ? '#475467' : '#667085', fontSize: '0.875rem' }}>…</span>
              : <button key={page} onClick={() => setCurrentPage(page)} style={btnStyle(page === currentPage, false)}>{page}</button>
            )}
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} style={btnStyle(false, currentPage === totalPages)}>Next →</button>
          </div>
        );
      })()}
    </div>
  );
};
